import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, PlayCircle, Quote, ShieldCheck, ArrowRight, X } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/motion";
import { CONTACT } from "@/data/content";
import useSEO from "@/hooks/useSEO";

/*
  Real Stories, Real Recovery
  Testimonials shared with informed consent — content provided by the client
  from their existing website.

  To embed a Google Drive video, set `driveId` on the story:
    driveId: "1AbCdEfGhI..."     // the file's Drive ID
  The card will render a "Play story" button that opens a lightbox with
  <iframe src="https://drive.google.com/file/d/{driveId}/preview">. The
  file must be shared as "Anyone with the link" for this to play publicly.
*/

const STORIES = [
  {
    name: "Randy",
    role: "Alumnus",
    pull: "I knew why I came here… and it was because of their strength; I got what I came to get.",
    body: "The staff were amazing — they listened, supported me, and genuinely cared about my well-being. The classes and environment helped me see things differently. I'm very happy I was here, and I believe this is a great first step for anyone serious about making a change.",
    videos: [
      { id: "1yq4YH0JDTXee9uEuewCFKVUVgs8OveIC", label: "Story 1" },
      { id: "1rYntpDKMbtiY4snpPznU1KQO8PcO2ASs", label: "Story 2" },
    ],
  },
  {
    name: "Ben",
    role: "Alumnus",
    pull: "I came in searching for answers. I left with structure, clarity, and a new way to live.",
    body: "From losing everything to rebuilding my life — this program helped me find hope, reconnect with my family, and get my life back.",
    videos: [
      { id: "1w4tgoaJGAB_2xPOnqw_0agFNfmd7vLfo", label: "Story 1" },
      { id: "1hiWNRRiBG99x-04uK_JFu99humJGlo8K", label: "Story 2" },
    ],
  },
  {
    name: "Mathew",
    role: "Alumnus",
    pull: "New Beginnings truly became a home for me.",
    body: "It's more than a recovery program — it became a home. A place to feel safe, reconnect with yourself, and discover joy again. I felt supported, came out of my shell, and even started dancing and enjoying the little things.",
    videos: [
      { id: "1b-SpYtVx_AONtUte2JCW9KYwxnYnWrJh", label: "Story" },
    ],
  },
  {
    name: "Shawn",
    role: "Alumnus",
    pull: "There are many places across the GTA — New Beginnings is a great place to start your recovery.",
    body: "The staff are supportive, approachable, and always willing to listen. You have access to counselling and professional support, with staff available around the clock. This is a once-in-a-lifetime opportunity, and I wouldn't trade the experience for anything.",
    videos: [
      { id: "1I5byfcV_XJMJLUheO2zHtWC6N5FvFbrd", label: "Story 1" },
      { id: "1GWbgzPo2lKe-kUM0g8YNIPSEwKkf3vLM", label: "Story 2" },
    ],
  },
  {
    name: "Judit",
    role: "Alumna",
    pull: "Healing isn't just about sobriety — it's about learning to relax, reconnect, and understand yourself.",
    body: "Meditation helped me relax, clear my mind, and reconnect with myself. The recovery education was invaluable — especially learning how the brain and body heal. The home is beautifully designed — it actually feels like a home, not a treatment centre.",
    videos: [
      { id: "1xcpTP1P4U7n7QRHPTdKCeU09TiydiRGh", label: "Story 1" },
      { id: "1Dn5ToEL5kt1QDcbibDR933Zc5l7IScXT", label: "Story 2" },
    ],
  },
  {
    name: "James",
    role: "Alumnus",
    pull: "Recovery is more than leaving addiction behind — it's learning to believe in yourself again.",
    body: "When I first came here, I felt ashamed, isolated, and disconnected. Everything starts with how you feel about yourself. New Beginnings gave me the foundation and confidence to move forward. I'm leaving stronger and ready for what comes next.",
    videos: [
      { id: "12lAQ7VF0R0zeyrhQKTKsA8jPE0lwY2Y8", label: "Story 1" },
      { id: "1v2dAqa7zKLeS61-JNFKPkGoOAFQ1GzDK", label: "Story 2" },
    ],
  },
  {
    name: "Berlin",
    role: "Alumnus",
    pull: "After 28 days at New Beginnings, I'm a completely different person.",
    body: "I've learned so much, overcome challenges, and gained the confidence to face the world. From the moment I arrived, I was amazed by how welcoming the home was — the peaceful neighborhood, big windows, backyard, and the Bluffs nearby made it feel like a breath of fresh air.",
    videos: [
      { id: "1TsueDHjjDn-e1y1m2G8Q0HHGiiF2Y_rB", label: "Story 1" },
      { id: "1aq87StWkT1Ail5_94vV9nRvRp1mUz3IS", label: "Story 2" },
    ],
  },
  {
    name: "Clinton",
    role: "Alumnus",
    pull: "After 28 days, I feel more hopeful and at peace with myself.",
    body: "The shame and guilt I carried have started to lift. Daily groups, one-on-one counselling, and support from the staff helped me understand that I wasn't alone. I built a relapse-prevention plan and rebuilt my family relationships. For the first time in a long time, I believe real change is possible.",
    videos: [
      { id: "1Ae3HNzQNZbI_dmEWAn220m4gxk6aGCtr", label: "Story 1" },
      { id: "1nVb83KeSYV3nx7xNLeFjVuceD7YQ3gjK", label: "Story 2" },
    ],
  },
  {
    name: "Arya",
    role: "Alumna",
    pull: "This place saved my life.",
    body: "This program gave me hope when I didn't know where my life was going. It gave me new tools for my recovery, helped me grow spiritually, and gave me a supportive circle I can rely on. Recovery is more than just staying sober — it's a new way to live, with hope, purpose, and strength.",
    videos: [
      { id: "1I6zCvnLoOtXo3QH-7uU75eU5PSsW2_ix", label: "Story 1" },
      { id: "1X7U62f2_nZMktI7rBVasmwsb1_MGxp_b", label: "Story 2" },
    ],
  },
  {
    name: "Arranda",
    role: "Alumna",
    pull: "Completing these 28 days showed me that I can do this.",
    body: "I came into this program unsure if I could complete another treatment program. Here, I found the support and encouragement I needed to rebuild my confidence. We explored mindfulness, codependency, boundaries, and so much more — new tools that were genuinely helpful in my recovery.",
    videos: [
      { id: "1vBQQiIxTGL5qgQgDIvKwB-ZE98EkdCh8", label: "Story 1" },
      { id: "1e7dOlB8qso5m2BvOtI4M6ijm5HMjxJoL", label: "Story 2" },
    ],
  },
];

const StoryCard = ({ s, i, onPlay }) => (
  <Reveal delay={(i % 3) * 0.08}>
    <article
      data-testid={`story-${s.name.toLowerCase()}`}
      className="group relative h-full rounded-2xl bg-white border border-line p-7 md:p-8 shadow-[0_1px_0_rgba(0,0,0,0.03)] hover:shadow-[0_20px_50px_-20px_rgba(46,106,138,0.35)] transition-shadow"
    >
      <span className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-terracotta/70" aria-hidden />
      <Quote className="w-8 h-8 text-terracotta/70" />
      <p className="mt-4 font-serif text-xl md:text-2xl text-forest leading-snug tracking-tight">
        &ldquo;{s.pull}&rdquo;
      </p>
      <p className="mt-4 text-ink2 text-[15px] leading-relaxed">{s.body}</p>
      <div className="mt-6 pt-5 border-t border-line flex items-center justify-between gap-3">
        <div>
          <p className="font-serif text-forest text-lg leading-none">— {s.name}</p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-ink2">{s.role}</p>
        </div>
        {s.videos && s.videos.length > 0 ? (
          <div className="flex flex-wrap gap-2 justify-end">
            {s.videos.map((v) => (
              <button
                key={v.id}
                onClick={() => onPlay({ ...s, driveId: v.id, label: v.label })}
                data-testid={`story-play-${s.name.toLowerCase()}-${v.label.toLowerCase().replace(/\s+/g,'-')}`}
                aria-label={`Play ${s.name} — ${v.label}`}
                className="relative group/thumb block w-24 h-16 rounded-lg overflow-hidden border border-line bg-forestdark shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <img
                  src={`https://drive.google.com/thumbnail?id=${v.id}&sz=w320`}
                  alt=""
                  className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-500"
                  loading="lazy"
                  draggable={false}
                  onError={(e) => { e.currentTarget.style.opacity = "0"; }}
                />
                <span className="absolute inset-0 bg-black/25 group-hover/thumb:bg-black/10 transition-colors" />
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-terracotta/95 text-white shadow-md group-hover/thumb:scale-110 transition-transform">
                    <PlayCircle className="w-4 h-4" />
                  </span>
                </span>
                <span className="absolute bottom-1 left-1.5 text-[9px] font-bold uppercase tracking-wider text-white drop-shadow">{v.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sand2 border border-line px-3 py-1.5 text-[11px] text-ink2">
            <PlayCircle className="w-3.5 h-3.5 text-terracotta" /> Video soon
          </span>
        )}
      </div>
    </article>
  </Reveal>
);

const VideoLightbox = ({ story, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      data-testid="story-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] grid place-items-center bg-black/80 backdrop-blur-sm p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden bg-black shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <button
          onClick={onClose}
          aria-label="Close video"
          data-testid="story-lightbox-close"
          className="absolute top-3 right-3 z-10 grid place-items-center w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <iframe
          src={`https://drive.google.com/file/d/${story.driveId}/preview`}
          title={`${story.name} — Real Story`}
          className="w-full h-full"
          allow="autoplay; encrypted-media"
          allowFullScreen
        />
      </div>
      <p className="mt-4 text-white/85 font-serif text-lg">
        {story.name}{story.label ? ` — ${story.label}` : ""}
      </p>
    </div>
  );
};

const StoriesPage = () => {
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(null);
  useSEO({
    title: "Real Stories, Real Recovery — New Beginnings Recovery Home Toronto",
    description:
      "Read first-person testimonials from graduates of New Beginnings Recovery Home in Toronto. Real people, real recovery — from inpatient drug & alcohol addiction treatment to lifetime sobriety.",
    keywords: "rehab testimonials Toronto, recovery stories, addiction treatment success stories, New Beginnings Recovery reviews",
    canonical: "https://newbeginningsrecovery.ca/stories",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="stories-page" className="bg-sand min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 md:pt-48 pb-16 md:pb-20 bg-forestdark text-sand relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
          aria-hidden
        />
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 relative">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] text-terracotta font-semibold mb-4 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Voices of hope
            </p>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
              Real Stories, <span className="italic text-terracotta">Real Recovery</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sand/85 text-lg leading-relaxed">
              Hear directly from individuals and families who have experienced the care, structure,
              and life-changing support at New Beginnings Recovery Home.
            </p>
            <p className="mt-4 text-sand/60 text-sm">
              All testimonials are shared with informed consent. Individual results may vary.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Stories grid */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1300px] px-5 md:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STORIES.map((s, i) => (
              <StoryCard key={s.name} s={s} i={i} onPlay={setPlaying} />
            ))}
          </div>
        </div>
      </section>

      {/* Video lightbox */}
      {playing && <VideoLightbox story={playing} onClose={() => setPlaying(null)} />}

      {/* Closing quote — Shaun S */}
      <section className="bg-forest text-sand py-20 md:py-28">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10 text-center">
          <Quote className="w-10 h-10 text-terracotta/80 mx-auto" />
          <p className="mt-6 font-serif text-2xl md:text-4xl leading-snug tracking-tight">
            &ldquo;Recovery has taught me that the darkest nights can lead to the brightest days.
            I am proof that hope and healing are real.&rdquo;
          </p>
          <p className="mt-6 text-terracotta uppercase tracking-[0.28em] text-xs font-semibold">— Shaun S. · Member of Recovery</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-sand2">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-forest tracking-tight">Have a question?</h2>
          <p className="mt-3 text-ink2 text-lg">Reach out to us. We are here to help — 24/7.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="stories-call"
              className="h-12 px-6 rounded-full bg-terracotta hover:bg-[#a8664b] text-white font-semibold flex items-center gap-2 transition-colors"
            >
              <Phone className="w-4 h-4" /> {CONTACT.phone}
            </a>
            <Button
              onClick={() => navigate("/contact")}
              data-testid="stories-message"
              variant="outline"
              className="h-12 px-6 rounded-full border-forest text-forest hover:bg-forest hover:text-sand font-semibold"
            >
              Message Us <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StoriesPage;
